<template>
  <div class="action-panel filters">
    <div class="input-wrapper">
      <SearchIcon />
      <input
        v-model="name"
        type="text"
        class="search"
        placeholder="Поиск по разделу"
        @input="emit('name-change', name)"
      />
    </div>
    <v-select
      v-if="pointType !== 'notes'"
      :model-value="type || null"
      variant="outlined"
      :items="templatesOptions"
      placeholder="Все статусы"
      item-title="label"
      item-value="id"
      class="select"
      clearable
      @update:model-value="handleChangeValue"
    />
    <v-select
      :model-value="pointType || null"
      variant="outlined"
      clearable
      :items="pointOptions"
      placeholder="Все задачи"
      item-title="label"
      item-value="name"
      class="select"
      :disabled="route.name === 'tasks-archive-list'"
      @update:model-value="handleChangePointsType"
    />
  </div>
</template>
<script setup>
import SearchIcon from '@/components/Icons/SearchIcon.vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { STATUSES } from '@/constants/points'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

const store = useStore()

const props = defineProps({
  initialName: {
    type: String,
    default: '',
  },
  initialType: {
    type: [String, Number],
    default: null,
  },
  initialPointType: {
    type: [String, Object],
    default: null,
  },
})

const emit = defineEmits(['name-change', 'type-change', 'point-type-change'])
const route = useRoute()

const isExpert = computed(() => store.getters['user/isExpert'])

const getPointTypeValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return route.name === 'tasks-archive-list' ? 'archive' : ''
  }
  if (typeof value === 'string') {
    return value
  }
  return value?.name || ''
}

const type = ref(props.initialType)
const name = ref(props.initialName)
const pointType = ref(getPointTypeValue(props.initialPointType))

const templatesOptions = ref(STATUSES)
const pointOptions = ref([
  {
    label: 'Требуют действий',
    name: 'active',
  },
  {
    label: 'Отработаны',
    name: 'archive',
  },
  {
    name: 'expired',
    label: 'Просроченные',
  },
  {
    name: 'notes',
    label: 'Черновики',
  },
])

const handleChangeValue = (val) => {
  type.value = val === null || val === undefined ? null : val
  emit('type-change', type.value)
}

const handleChangePointsType = (val) => {
  if (val === null || val === undefined || val === '' || (typeof val === 'object' && !val?.name)) {
    const newValue = route.name === 'tasks-archive-list' ? 'archive' : ''
    pointType.value = newValue
    emit('point-type-change', newValue)
    return
  }
  const stringValue = typeof val === 'string' ? val : val?.name || ''
  if (stringValue && pointOptions.value.some((opt) => opt.name === stringValue)) {
    pointType.value = stringValue
    emit('point-type-change', stringValue)
  } else {
    const newValue = route.name === 'tasks-archive-list' ? 'archive' : ''
    pointType.value = newValue
    emit('point-type-change', newValue)
  }
}

watch(
  () => props.initialName,
  (newVal) => {
    if (newVal !== name.value) {
      name.value = newVal || ''
    }
  },
)

watch(
  () => props.initialType,
  (newVal) => {
    if (newVal !== type.value) {
      type.value = newVal || null
    }
  },
)

watch(
  () => props.initialPointType,
  (newVal) => {
    const pointTypeValue = getPointTypeValue(newVal)
    if (pointTypeValue !== pointType.value) {
      pointType.value = pointTypeValue
    }
  },
)

onMounted(() => {
  if (route.name === 'tasks-archive-list') {
    if (pointType.value !== 'archive') {
      pointType.value = 'archive'
      emit('point-type-change', 'archive')
    }
  } else if (
    props.initialPointType !== null &&
    props.initialPointType !== undefined &&
    props.initialPointType !== ''
  ) {
    const pointTypeValue = getPointTypeValue(props.initialPointType)
    if (pointTypeValue && pointTypeValue !== pointType.value) {
      pointType.value = pointTypeValue
      emit('point-type-change', pointTypeValue)
    }
  }
  if (props.initialName && props.initialName !== name.value) {
    emit('name-change', props.initialName)
  }
  if (
    props.initialType !== null &&
    props.initialType !== undefined &&
    props.initialType !== type.value
  ) {
    emit('type-change', props.initialType)
  }
  nextTick(() => {
    if (isExpert.value) {
      pointOptions.value.push({ label: 'Экспертиза', name: 'expert' })
    }
  })
})
</script>
<style lang="scss" scoped>
.action-panel {
  padding: 12px 24px;
  border-bottom: 1px solid rgba(242, 242, 242, 1);
  margin-bottom: 24px;

  .select {
    max-width: 240px;

    @media (max-width: 567px) {
      max-width: 100%;
      margin-bottom: 10px;
    }
  }

  .checkbox {
    margin-bottom: 0;
    max-width: 136px;

    .v-label {
      font-size: 14px;
    }
  }
}
</style>
<style lang="scss">
.action-panel {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 30px;

  @media (max-width: 765px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  @media (max-width: 567px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .input-wrapper {
    background: var(--bage-color);
    border: 1px solid var(--bage-color);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    height: 40px;
    border-radius: 8px;
    flex: 1;
    max-width: 640px;

    @media (max-width: 567px) {
      margin-bottom: 10px;
    }

    input {
      height: 100%;
      min-height: 40px;
      display: block;
      flex: 1;
      font-size: 14px;
    }
  }

  .v-input--density-default {
    --v-input-control-height: 40px;
    --v-input-padding-top: 12px;
  }

  .v-input__details {
    display: none;
  }

  .checkbox {
    .v-icon--size-default {
      font-size: 24px;
    }
  }

  .select {
    max-width: 194px;
    width: 100%;

    @media (max-width: 765px) {
      max-width: 100%;
    }

    .v-field {
      border-radius: 8px;
    }
    .v-field__input {
      padding: 10px 12px;
      overflow: hidden;

      span {
        font-size: 14px;
        line-height: 20px;
        white-space: pre;
      }
    }
  }
}
</style>
